import React, { useState, useEffect } from "react";
import styles from "./all-students-tab.module.css";
import { Popconfirm, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import CustomTextfield from "../../../../common/input/custom-textfield/custom-textfield";
import SVGIcon from "../../../../constants/SVGIcon";
import CustomTable from "../../../../common/custom-table/custom-table";
import RoutesConstant from "../../../../constants/routes";
import { deleteStudent, exportStudents, fetchStudentList } from "../../../../services/student";
import CustomProfileAvatar from "../../../../common/custom-profile-avatar/custom-profile-avatar";
import { resendCredentialsStudents } from "../../../../services/student";
import { formatDate } from "../../../../utils/format-date/format-date";
import { toast } from "react-toastify";
import CustomButton from "../../../../common/custom-button/custom-button";
import { downloadFile } from "../../../../utils/common";

const AllStudentsList = ({ triggerRefetch, isActive = true, selectedYear }) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [studentList, setStudentList] = useState([]);

  const columns = [
    {
      title: "Reg No.",
      dataIndex: "user_name",
      key: "user_name",
      render: (value) => value ?? "-",
    },
    {
      title: "Student Name",
      dataIndex: "name",
      key: "name",
      render: (value, record) => (
        <div className={styles.nameContainer}>
          <CustomProfileAvatar
            image={
              record?.profile_picture
                ? record?.profile_picture
                : "sample-user.png"
            }
            imageName={record?.name}
            imageSrc={
              record?.profile_picture ?? "assets/images/sample-user.png"
            }
            divBackgroundColor={"#97a997"}
          />
          <Typography>{value}</Typography>
        </div>
      ),
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      render: (value) => value ?? "-",
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      render: (_, record) => record?.classroom?.level?.name ?? "-",
    },
    {
      title: "Class",
      dataIndex: "class",
      key: "class",
      render: (_, record) => record?.classroom?.name ?? "-",
    },
    {
      title: "Date of birth",
      dataIndex: "date_of_birth",
      key: "date_of_birth",
      render: (value) => (value ? formatDate(value) : "-"),
    },
    {
      title: "Parent Phone No.",
      dataIndex: "parent_phone_number",
      key: "parent_phone_number",
      render: (_, record) =>
        record?.parents?.length ? record?.parents[0]?.phone_number : "-",
    },
  ];

  const handleDeleteStudent = async (studentId) => {
    try {
      setLoading(true);
      await deleteStudent(studentId);
      await getStudentList();
      toast.success("Student is deleted");
    } catch (err) {
      toast.error("Error Deleting Student");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCredentials = async (studentId) => {
    try {
      setLoading(true);
      await resendCredentialsStudents({
        student_id: studentId,
        classroom_id: "",
      });
      toast.success("Resend is Successful");
    } catch (err) {
      toast.success("Error in Resending Credentials");
    } finally {
      setLoading(false);
    }
  };
  const handleResendAllCredentials = async () => {
    try {
      setLoading(true);
      await resendCredentialsStudents({ student_id: "", classroom_id: "" });
      toast.success("ResendAll is Successful");
    } catch (err) {
      toast.success("Error in ResendingAll Credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleExportAllStudents = async () => {
    try {
      setLoading(true);
      const payload = { academic_year_id: selectedYear };
      const response = await exportStudents(payload);
      const contentDisposition = response.headers["content-disposition"];
      downloadFile(response.data, contentDisposition, "students.csv");
      toast.success("All Students exported Successfully");
    } catch (error) {
      console.error("Failed to export students:", error);
      toast.error(error?.data?.message || "Failed to export students");
    } finally {
      setLoading(false);
    }
  };

  const menuItems = (record) => [
    {
      key: "1",
      label: "View Details",
      onClick: () =>
        navigate(`${RoutesConstant.STUDENT_DETAILS}?id=${record?.id}`),
    },
    {
      key: "2",
      label: "Edit",
      onClick: () =>
        navigate(`${RoutesConstant.STUDENT_FORM}?uid=${record?.id}`),
    },
    {
      key: "3",
      label: (
        <Popconfirm
          title={`Are you sure you want to delete ${record?.name}?`}
          onConfirm={() => handleDeleteStudent(record?.id)}
          okText="Yes"
          cancelText="No"
        >
          <span>Delete</span>
        </Popconfirm>
      ),
    },
    {
      key: "4",
      label: (
        <Popconfirm
          title={`Are you sure you want to Resend credentials to ${record?.name}?`}
          onConfirm={() => handleResendCredentials(record?.id)}
          okText="Yes"
          cancelText="No"
        >
          <span>Resend Credentials</span>
        </Popconfirm>
      ),
    },
  ];

  useEffect(() => {
    if (selectedYear) {
      const timeOut = setTimeout(() => {
        setPageNo(1);
        getStudentList();
      }, 500);
      return () => {
        clearTimeout(timeOut);
      };
    }
  }, [searchTerm, triggerRefetch, selectedYear]);

  const getStudentList = async (tempPage = pageNo, tempPageSize = pageSize) => {
    try {
      setLoading(true);
      const payload = {
        page: tempPage,
        pageSize: tempPageSize,
        academic_year_id: selectedYear,
      };

      if (searchTerm) {
        payload.search = searchTerm;
      }
      if (!isActive) {
        payload.is_active = "0";
      }
      const response = await fetchStudentList(payload);
      if (response?.data) {
        setStudentList(response?.data?.data?.students);
        setTotalCount(response?.data?.data?.totalRecords);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log("error", error);
    }
  };

  const handleChangePage = (page, pageSize) => {
    setPageNo(page);
    setPageSize(pageSize);
    getStudentList(page, pageSize);
  };

  const handleSeach = (e) => {
    setPageNo(1);
    setSearchTerm(e?.target?.value);
  };

  return (
    <div className={styles.main_container}>
      <div className={styles.title_container}>
        <Typography className={styles.title}>
          {`${isActive ? "Active" : "Suspended"} Students`}
        </Typography>
        <div className={styles.view_style_options}>
          <div className={styles.filter_container}>
            {isActive && studentList?.length ? (
              <>
                <CustomButton
                  title="Export All Students"
                  onClick={handleExportAllStudents}
                />
                <CustomButton
                  title="Resend All Credentials"
                  onClick={() => {
                    handleResendAllCredentials();
                  }}
                />
              </>
            ) : null}
            <CustomTextfield
              id={"search"}
              type={"text"}
              className={styles.inputField}
              value={searchTerm}
              onChange={handleSeach}
              suffix={
                <SVGIcon
                  name={"search-icon"}
                  width={"24"}
                  height={"24"}
                  className="anticon"
                  stroke={"#667085"}
                />
              }
              placeholder={"Search ..."}
            />
          </div>
        </div>
      </div>
      <CustomTable
        columns={columns}
        dataSource={studentList}
        total={totalCount}
        loading={loading}
        showMoreIcon={true}
        menuItems={menuItems}
        pageSize={pageSize}
        currentPage={pageNo}
        handlePageChange={handleChangePage}
      />
    </div>
  );
};

export default AllStudentsList;
